/* In-memory ConfigClient for mock mode (no gateway configured). Seeded with a
 * few illustrative profiles and fully mutable, so the config UI is exercisable
 * without a backend. Errors mirror the contract `{ error }` envelope. */

import type { ConfigClient } from "./ConfigClient";
import type {
  ConfigProfile,
  ConfigProfileCreate,
  ConfigProfileUpdate,
  ConfigSchema,
} from "./types";
import type { ApiError } from "../analytics";
import { DASHBOARD_NOW } from "../analytics";
import { AGENTS, AGENT_CONFIG_PRESET } from "../analytics/mock/catalog";
import { staticConfigSchema } from "./schema";
import {
  applyDisableAllTransformers,
  applyEnableAll,
  applyLosslessOnly,
  defaultValues,
  overridesFromValues,
  setFamilyEnabled,
  type ConfigValues,
} from "./overrides";

function notFound(configId: string): { error: ApiError } {
  return {
    error: { code: "not_found", message: `config_id '${configId}' not found` },
  };
}

function pseudoUuid(seed: string): string {
  let h = 0x811c9dc5;
  const hex = (n: number) => {
    const out: string[] = [];
    for (let i = 0; i < n; i += 1) {
      h = Math.imul(h ^ seed.charCodeAt(i % seed.length), 0x01000193) >>> 0;
      out.push(((h >>> (i % 24)) & 0xf).toString(16));
    }
    return out.join("");
  };
  return `${hex(8)}-${hex(4)}-4${hex(3)}-${hex(4)}-${hex(12)}`;
}

const NOW = DASHBOARD_NOW.toISOString();

/** Map a preset key to a value map. Used for both agent-owned configs and the
 *  standalone library examples. */
function presetValues(preset: string): ConfigValues {
  const base = defaultValues();
  if (preset === "everything") return applyEnableAll(base);

  if (preset === "lossless") return applyLosslessOnly(base);

  if (preset === "tool-results") {
    return setFamilyEnabled(applyLosslessOnly(base), "tool_results", true);
  }

  if (preset === "baseline") return applyDisableAllTransformers(base);

  return base;
}

function mk(
  config_id: string,
  name: string,
  description: string,
  preset: string,
): ConfigProfile {
  return {
    id: pseudoUuid(config_id),
    config_id,
    name,
    description,
    overrides: overridesFromValues(presetValues(preset)),
    enabled: true,
    created_at: NOW,
    updated_at: NOW,
  };
}

function seedProfiles(): ConfigProfile[] {
  // One profile per agent that owns a config (config_id === agent_id), so the
  // 1:1 UI and the library stay consistent…
  const owned = AGENTS.filter((a) => a.config_id).map((a) =>
    mk(
      a.agent_id,
      a.name,
      `Config for ${a.name}.`,
      AGENT_CONFIG_PRESET[a.agent_id] ?? "lossless",
    ),
  );
  // …plus a couple of standalone library configs not bound to any agent (the
  // secondary use the library still supports).
  const standalone = [
    mk(
      "everything-on",
      "Everything on",
      "Every transformer enabled — a reusable starting point.",
      "everything",
    ),
    mk(
      "lossless-baseline",
      "Lossless baseline",
      "Only cache-safe, lossless transformers.",
      "lossless",
    ),
  ];
  return [...owned, ...standalone];
}

export interface MockConfigClientOptions {
  delayMs?: number;
}

export class MockConfigClient implements ConfigClient {
  private readonly delayMs: number;
  private profiles: Map<string, ConfigProfile>;

  constructor(opts: MockConfigClientOptions = {}) {
    this.delayMs = opts.delayMs ?? 180;
    this.profiles = new Map(seedProfiles().map((p) => [p.config_id, p]));
  }

  private async delay(): Promise<void> {
    if (this.delayMs > 0) await new Promise((r) => setTimeout(r, this.delayMs));
  }

  async list(): Promise<ConfigProfile[]> {
    await this.delay();
    return [...this.profiles.values()].sort((a, b) =>
      a.config_id.localeCompare(b.config_id),
    );
  }

  async getSchema(): Promise<ConfigSchema> {
    await this.delay();
    // Derived from the static descriptor, so a mock overlay resolves to exactly
    // the same defaults the UI would use without the gateway.
    return staticConfigSchema();
  }

  async get(configId: string): Promise<ConfigProfile> {
    await this.delay();
    const row = this.profiles.get(configId);
    if (!row) throw notFound(configId);

    return row;
  }

  async create(payload: ConfigProfileCreate): Promise<ConfigProfile> {
    await this.delay();
    if (this.profiles.has(payload.config_id)) {
      throw {
        error: {
          code: "bad_request",
          message: `config_id '${payload.config_id}' already exists`,
        } satisfies ApiError,
      };
    }

    const row: ConfigProfile = {
      id: pseudoUuid(payload.config_id),
      config_id: payload.config_id,
      name: payload.name ?? "",
      description: payload.description ?? null,
      overrides: payload.overrides ?? {},
      enabled: payload.enabled ?? true,
      created_at: NOW,
      updated_at: NOW,
    };
    this.profiles.set(row.config_id, row);
    return row;
  }

  async update(
    configId: string,
    payload: ConfigProfileUpdate,
  ): Promise<ConfigProfile> {
    await this.delay();
    const row = this.profiles.get(configId);
    if (!row) throw notFound(configId);

    const next: ConfigProfile = {
      ...row,
      name: payload.name ?? row.name,
      description:
        payload.description === undefined
          ? row.description
          : payload.description,
      overrides: payload.overrides ?? row.overrides,
      enabled: payload.enabled ?? row.enabled,
      updated_at: NOW,
    };
    this.profiles.set(configId, next);
    return next;
  }

  async remove(configId: string): Promise<void> {
    await this.delay();
    if (!this.profiles.has(configId)) throw notFound(configId);

    this.profiles.delete(configId);
  }
}
